// Shared configuration for every scorecard grid (outdoor, indoor, team and full
// views). The formik field name is `data[<rowIndex>]<attribute_key>` on every
// screen, so one description of the columns serves all of them.

// Discipline bands shown above the column headers. This is the UNION of the
// outdoor and indoor key sets - a key that a given game does not use simply
// isn't found and drops out, so the same list works for both.
export const STAT_GROUPS = [
  {
    key: 'batting',
    label: 'Batting',
    keys: [
      'rs',
      'bf',
      'ones',
      'twos',
      'threes',
      'fours',
      'fives',
      'sixes',
      'sevens',
      'eights',
      'nines',
      'outs',
      'dotp',
      'dks',
      'no',
      'hwot',
    ],
  },
  {
    key: 'bowling',
    label: 'Bowling',
    keys: [
      'overs',
      'mdns',
      'run',
      'wks',
      'dotb',
      'hattrick',
      'wb',
      'wct',
      'wcwk',
      'wlbw',
      'wst',
      'whw',
    ],
  },
  { key: 'fielding', label: 'Fielding', keys: ['cs', 'cwks', 'sts', 'rods', 'roas'] },
]

// Which control a column gets. Mirrors the original dropdownCol1..4 buckets:
// one bucket was a Yes/No select, one the dismissal select, and the rest plain
// number inputs - so anything unrecognised falls through to a number.
export const YES_NO_KEYS = ['dks', 'hattrick', 'no']
export const DISMISSAL_KEYS = ['hwot']

export const DISMISSAL_OPTIONS = [
  { value: '', label: 'Select Dismissal' },
  { value: 'hobwl', label: 'Bowled' },
  { value: 'hoct', label: 'Catch' },
  { value: 'hocwk', label: 'Catch WK' },
  { value: 'holbw', label: 'LBW' },
  { value: 'host', label: 'Stump' },
  { value: 'horod', label: 'RO-Direct' },
  { value: 'horoa', label: 'RO-Assist' },
  { value: 'hohw', label: 'Hit Wicket' },
  { value: 'hohb', label: 'Handled-Ball' },
  { value: 'hoobs', label: 'Obstruct' },
  { value: 'hotmd', label: 'Timed' },
]

export const POSITION_META = {
  1: { label: 'BAT' },
  2: { label: 'BOWL' },
  3: { label: 'AR' },
  4: { label: 'WK' },
}

// Indoor screens used to hardcode these 33 columns in the markup, in this
// order. Kept as the fallback for when the API sends no column list, so an
// indoor scorecard can never render with nothing to type into.
export const INDOOR_COLUMNS = [
  { attribute_key: 'rs', label: 'RS', full_name: 'Runs' },
  { attribute_key: 'bf', label: 'BF', full_name: 'Balls Faced' },
  { attribute_key: 'ones', label: '1s', full_name: 'Scores of 1' },
  { attribute_key: 'twos', label: '2s', full_name: 'Scores of 2' },
  { attribute_key: 'threes', label: '3s', full_name: 'Scores of 3' },
  { attribute_key: 'fours', label: '4s', full_name: 'Scores of 4' },
  { attribute_key: 'fives', label: '5s', full_name: 'Scores of 5' },
  { attribute_key: 'sixes', label: '6s', full_name: 'Scores of 6' },
  { attribute_key: 'sevens', label: '7s', full_name: 'Scores of 7' },
  { attribute_key: 'eights', label: '8s', full_name: 'Scores of 8' },
  { attribute_key: 'nines', label: '9s', full_name: 'Scores of 9' },
  { attribute_key: 'outs', label: 'Outs', full_name: 'Number of Outs' },
  { attribute_key: 'overs', label: 'OVRS', full_name: 'Overs' },
  { attribute_key: 'mdns', label: 'MDNS', full_name: 'Maidens' },
  { attribute_key: 'run', label: 'RG', full_name: 'Runs Given' },
  { attribute_key: 'wks', label: 'WKTS', full_name: 'Wickets' },
  { attribute_key: 'cs', label: 'CS', full_name: 'Catches' },
  { attribute_key: 'cwks', label: 'CWK', full_name: 'Catches (Wicket-Keeper)' },
  { attribute_key: 'sts', label: 'ST', full_name: 'Stumpings' },
  { attribute_key: 'rods', label: 'ROD', full_name: 'Run-Out Direct' },
  { attribute_key: 'roas', label: 'ROA', full_name: 'Run-Out Assisted' },
  { attribute_key: 'dks', label: 'DK', full_name: 'Duck' },
  { attribute_key: 'hattrick', label: 'HT', full_name: 'Hat-Trick' },
  { attribute_key: 'no', label: 'NO', full_name: 'Not Out' },
  { attribute_key: 'hwot', label: 'HWOT', full_name: 'How Out (Batting)' },
  { attribute_key: 'wb', label: 'WB', full_name: 'Wicket Dismissal (Bowled)' },
  { attribute_key: 'wct', label: 'WCT', full_name: 'Wicket Dismissal (Caught)' },
  { attribute_key: 'wcwk', label: 'WCWK', full_name: 'Wicket Dismissal (Caught WK)' },
  { attribute_key: 'wlbw', label: 'WLBW', full_name: 'Wicket Dismissal (LBW)' },
  { attribute_key: 'wst', label: 'WST', full_name: 'Wicket Dismissal (Stump)' },
  { attribute_key: 'whw', label: 'WHW', full_name: 'Wicket Dismissal (Hit Wicket)' },
  { attribute_key: 'dotp', label: 'DOTP', full_name: 'Dots Played' },
  { attribute_key: 'dotb', label: 'DOTB', full_name: 'Dots Bowled' },
]

// Bands the columns by discipline. Every column renders exactly once: whatever
// no band claims is swept into "Other", so a column added server-side can never
// silently vanish from the grid.
export const groupColumns = (columns) => {
  const list = columns || []
  const claimed = new Set()
  const groups = STAT_GROUPS.map((group) => {
    const items = group.keys
      .map((key) => list.find((column) => column?.attribute_key === key))
      .filter(Boolean)
    items.forEach((column) => claimed.add(column.attribute_key))
    return { ...group, columns: items }
  })
  const rest = list.filter((column) => column?.attribute_key && !claimed.has(column.attribute_key))
  if (rest.length) groups.push({ key: 'other', label: 'Other', columns: rest })
  return groups.filter((group) => group.columns.length)
}

export const hasScores = (item, columns) =>
  (columns || []).some((column) => {
    const value = item?.[column?.attribute_key]
    if (value === null || value === undefined || value === '') return false
    return Number(value) !== 0
  })
